import React, { useEffect, useState, useMemo } from "react";
import { PieChart } from "@mui/x-charts/PieChart";
import baseUrl from "../../../../api/baseUrl";
import PieChartIcon from "@mui/icons-material/PieChart";

const TreatmentPieChart = React.memo(({ theme = "light" }: { theme?: "light" | "dark" }) => {
  const [chart, setchart] = useState<any[]>([]);
  const [chartdata, setchartdata] = useState({
    c1: [],
    c2: [],
  });
  const [select, setselect] = useState("month");
  const isDark = theme === "dark";

  useEffect(() => {
    baseUrl
      .get("client/dashboard/findTreatmentPieChart")
      .then((response) => {
        setchart(response.data.data);
        setchartdata({
          c1: response.data.data,
          c2: response.data.treatmenttoday,
        });
      })
      .catch((error) => {
        console.error(error.response);
      });
  }, []);

  const handleChange = (value: string) => {
    setselect(value);
    if (value === "month") {
      setchart(chartdata.c1);
    } else {
      setchart(chartdata.c2);
    }
  };

  // Calculate total and percentages
  const { total, chartWithPercentage } = useMemo(() => {
    const total = chart.reduce((sum, item) => sum + (item.value || 0), 0);
    const chartWithPercentage = chart.map((item) => ({
      ...item,
      percentage: total > 0 ? ((item.value / total) * 100).toFixed(1) : 0,
    }));
    return { total, chartWithPercentage };
  }, [chart]);

  return (
    <div className={`rounded-2xl shadow-lg border overflow-hidden h-full transition-all duration-300 ${
      isDark 
        ? "bg-slate-800 border-slate-700 shadow-slate-900/50" 
        : "bg-white border-slate-100 shadow-slate-200/50"
    }`}>
      {/* Header */}
      <div className={`px-6 py-4 border-b flex items-center justify-between ${
        isDark ? "border-slate-700" : "border-slate-100"
      }`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
            <PieChartIcon className="text-white" sx={{ fontSize: 20 }} />
          </div>
          <h3 className={`text-lg font-bold ${
            isDark ? "text-slate-100" : "text-slate-800"
          }`}>Treatment Distribution</h3>
        </div>
        <div className={`flex gap-1 p-1 rounded-lg ${
          isDark ? "bg-slate-700" : "bg-slate-100"
        }`}>
          <button
            onClick={() => handleChange("month")}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
              select === "month"
                ? isDark
                  ? "bg-slate-600 text-emerald-300 shadow-sm"
                  : "bg-white text-emerald-600 shadow-sm"
                : isDark
                ? "text-slate-400 hover:text-slate-200"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            Month
          </button>
          <button
            onClick={() => handleChange("week")}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
              select === "week"
                ? isDark
                  ? "bg-slate-600 text-emerald-300 shadow-sm"
                  : "bg-white text-emerald-600 shadow-sm"
                : isDark
                ? "text-slate-400 hover:text-slate-200"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            Week
          </button>
        </div>
      </div>

      {/* Chart & Details */}
      <div className="p-4">
        {chart?.length === 0 ? (
          <div className={`py-16 text-center ${
            isDark ? "text-slate-500" : "text-slate-400"
          }`}>
            <PieChartIcon sx={{ fontSize: 48 }} className="mb-2" />
            <p>No treatment data available</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Pie Chart */}
            <div className="flex justify-center">
              <PieChart
                series={[
                  {
                    data: chart,
                    innerRadius: 40,
                    outerRadius: 80,
                    paddingAngle: 2,
                    cornerRadius: 4,
                  },
                ]}
                width={250}
                height={200}
                slotProps={{
                  legend: { hidden: true },
                }}
              />
            </div>

            {/* Total */}
            <div className={`text-center py-2 border-b ${
              isDark ? "border-slate-700" : "border-slate-200"
            }`}>
              <span className={`text-sm ${
                isDark ? "text-slate-400" : "text-slate-500"
              }`}>Total Treatments: </span>
              <span className={`text-lg font-bold ${
                isDark ? "text-emerald-400" : "text-emerald-600"
              }`}>{total}</span>
            </div>

            {/* Details Grid - 4 columns, scrollable */}
            <div className="max-h-[300px] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {chartWithPercentage.map((item: any, index: number) => (
                  <div
                    key={index}
                    className={`p-2 rounded-lg transition-colors text-center ${
                      isDark 
                        ? "bg-slate-700/50 hover:bg-slate-700" 
                        : "bg-slate-50 hover:bg-slate-100"
                    }`}
                  >
                    <div className="flex items-center justify-center gap-1.5 mb-1">
                      <div
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: item.color || `hsl(${index * 25}, 70%, 50%)` }}
                      />
                      <span className={`text-xs font-medium truncate ${
                        isDark ? "text-slate-300" : "text-slate-700"
                      }`}>
                        {item.label}
                      </span>
                    </div>
                    <div className="flex items-center justify-center gap-1">
                      <span className={`text-sm font-semibold ${
                        isDark ? "text-slate-200" : "text-slate-800"
                      }`}>{item.value}</span>
                      <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${
                        isDark 
                          ? "bg-emerald-900/30 text-emerald-400 border border-emerald-800/50" 
                          : "bg-emerald-100 text-emerald-700"
                      }`}>
                        {item.percentage}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default TreatmentPieChart;
